import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { postInitializeSchema } from '../helpers/sheetsApi';
import { formatSheetDisplayText, getSheetDisplayName } from '../helpers/sheetDisplay';

function GenerateSchema({ accessToken }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [info, setInfo] = useState('');
  const [result, setResult] = useState(null);

  async function handleGenerateSchema() {
    setLoading(true);
    setError('');
    setSuccess('');
    setInfo('');

    try {
      const data = await postInitializeSchema(accessToken);
      setResult(data);

      const rolesCreated = Boolean(data?.rolesSheet?.created);
      const referenceSheetsCreated = Array.isArray(data?.referenceSheets)
        ? data.referenceSheets.some((sheet) => Boolean(sheet?.created))
        : false;
      const usuariosCreated = Boolean(data?.usuariosSheet?.created);
      const requestNewUserCreated = Boolean(data?.requestNewUserSheet?.created);
      const requestRoleChangeCreated = Boolean(data?.requestRoleChangeSheet?.created);
      const requestDeleteUserCreated = Boolean(data?.requestDeleteUserSheet?.created);
      const requestMoveUserCreated = Boolean(data?.requestMoveUserSheet?.created);
      const requestUpdateUserInfoCreated = Boolean(data?.requestUpdateUserInfoSheet?.created);
      const notificationEmailsCreated = Boolean(data?.notificationEmailsSheet?.created);
      const allAlreadyExisted =
        !rolesCreated
        && !referenceSheetsCreated
        && !usuariosCreated
        && !requestNewUserCreated
        && !requestRoleChangeCreated
        && !requestDeleteUserCreated
        && !requestMoveUserCreated
        && !requestUpdateUserInfoCreated
        && !notificationEmailsCreated;

      if (allAlreadyExisted) {
        setInfo('Las hojas requeridas ya existen y sus columnas fueron verificadas y actualizadas.');
      } else {
        setSuccess('El schema fue verificado, se actualizaron las columnas y se crearon las hojas faltantes.');
      }
    } catch (requestError) {
      setResult(null);
      setError(requestError.message || 'No se pudo generar el schema.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box component="main" sx={{ width: '100%', p: { xs: 1.5, md: 2 } }}>
      <Paper variant="outlined" sx={{ p: 2, maxWidth: 760 }}>
        <Stack spacing={1.5}>
          <Typography variant="h6">Generar schema</Typography>
          <Typography sx={{ color: 'text.secondary' }}>
            Esta sección está pensada para usarse una sola vez. Verifica si existen las hojas
            <strong> Roles</strong>, las hojas de referencia de sedes e idioma,
            <strong> Usuarios</strong>, la hoja de correos de notificaciones y las hojas de solicitudes;
            si no existen, las crea con las columnas requeridas. También actualiza columnas administradas,
            como <strong>Solicitud ejecutada</strong> y <strong>Solicitudes en mi sede</strong> en la hoja Roles.
          </Typography>

          <Stack direction="row" spacing={1}>
            <Button
              type="button"
              variant="contained"
              onClick={handleGenerateSchema}
              disabled={loading}
            >
              {loading ? 'Generando...' : 'Generar schema'}
            </Button>
          </Stack>

          {error && <Alert severity="error">{formatSheetDisplayText(error)}</Alert>}
          {info && <Alert severity="info">{info}</Alert>}
          {success && <Alert severity="success">{success}</Alert>}

          {result && (
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Stack spacing={1}>
                <Typography sx={{ color: 'text.secondary' }}>
                  Estado de hojas:
                </Typography>
                <Typography>
                  Roles: {result.rolesSheet?.created ? 'creada' : 'ya existía'}
                </Typography>
                {(result.referenceSheets || []).map((sheet) => (
                  <Typography key={sheet.name}>
                    {getSheetDisplayName(sheet.name)}: {sheet.created ? 'creada' : 'ya existía'}
                  </Typography>
                ))}
                <Typography>
                  Usuarios: {result.usuariosSheet?.created ? 'creada' : 'ya existía'}
                </Typography>
                <Typography>
                  Solicitudes - Nuevo Usuario: {result.requestNewUserSheet?.created ? 'creada' : 'ya existía'}
                </Typography>
                <Typography>
                  Solicitudes - Cambio de Rol: {result.requestRoleChangeSheet?.created ? 'creada' : 'ya existía'}
                </Typography>
                <Typography>
                  Solicitudes - Eliminar Usuario: {result.requestDeleteUserSheet?.created ? 'creada' : 'ya existía'}
                </Typography>
                <Typography>
                  Solicitudes - Mover Usuario: {result.requestMoveUserSheet?.created ? 'creada' : 'ya existía'}
                </Typography>
                <Typography>
                  Solicitudes - Datos Personales: {result.requestUpdateUserInfoSheet?.created ? 'creada' : 'ya existía'}
                </Typography>
                <Typography>
                  {getSheetDisplayName(result.notificationEmailsSheet?.name || 'correos_notificaciones_usuarios')}: {result.notificationEmailsSheet?.created ? 'creada' : 'ya existía'}
                </Typography>
              </Stack>
            </Paper>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}

export default GenerateSchema;
